import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setGender, setLook, setIndex, setRandom } from './drawSlice';
import assets from '../../consume';

import skinImg from './img/skin.png';
import hairImg from './img/hair.png';
import mouthImg from './img/beard.png';
import addonImg from './img/addon.png';
import wearImg from './img/wear.png';
import arrowLeft from './img/arrow_left.png';
import arrowRight from './img/arrow_right.png';
import maleImg from './img/male.png';
import femaleImg from './img/female.png';
import randomImg from './img/random.png';

// Constants
const SKIN = 'skin';
const HAIR = 'hair';
const WEAR = 'wear';
const ADDON = 'addon';
const MOUTH = 'mouth';

type LayerType = typeof SKIN | typeof HAIR | typeof WEAR | typeof ADDON | typeof MOUTH;

const layerPeep: Record<LayerType, string> = {
  skin: '1gen',
  wear: '2wear',
  hair: '3hair',
  mouth: '4mouth',
  addon: '5addon'
};

// Styled components
const Forms = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 100,
  [theme.breakpoints.down('sm')]: {
    top: 10
  }
}));

const FormControl = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  justifyContent: 'space-between',
  maxWidth: 410,
  marginTop: 30,
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    marginTop: 55,
    '&.right': { right: 0 },
    '&.random': { display: 'flex', justifyContent: 'center', width: '100%' }
  }
}));

const Textures = styled('div')({
  margin: 'auto',
  display: 'flex',
  justifyContent: 'center',
  marginTop: 5
});

const Selection = styled('div')({
  display: 'flex',
  flexFlow: 'row wrap',
  maxWidth: 305,
  margin: 'auto',
  '& .labels': {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    '& .label': { width: '50%' }
  }
});

const SelectionControl = styled('div')({
  margin: 'auto',
  display: 'flex',
  justifyContent: 'center',
  maxWidth: 410,
});

const Button = styled('button')({
  background: 'transparent',
  border: 'transparent',
  cursor: 'pointer',
  outline: 'none',
  '&.small': {
    padding: 0,
    '& img': { opacity: 0.85 },
    '&.active, &:hover': {
      '& img': {
        filter: 'brightness(120%)',
        transform: 'scale(1.1)',
        opacity: 1,
      }
    },
    '&.active:hover img': {
      filter: 'brightness(100%)',
      transform: 'scale(1)',
    }
  }
});

const TextureButton = styled(Button)({
  '& img': { opacity: 0.85 },
  '&.active, &:hover': {
    '& img': {
      filter: 'brightness(150%)',
      transform: 'scale(1.2)',
      opacity: 1,
    }
  },
  '&.active:hover img': {
    filter: 'brightness(100%)',
    transform: 'scale(1)',
  }
});

const ColorEfxButton = styled(TextureButton)({
  '&.active img, &:hover img': {
    filter: 'invert(48%) sepia(13%) saturate(3207%) hue-rotate(100deg) brightness(95%) contrast(80%) !important',
  }
});

const SelectionText = styled(Typography)({
  color: '#54a936',
  fontWeight: 800,
  textAlign: 'center',
  marginTop: 10
});

const ColorBox = styled('div')({
  display: 'flex',
  padding: '5px 10px',
  justifyContent: 'center',
  margin: 'auto',
  maxWidth: 305,
  flexFlow: 'row wrap',
  '& .label': { width: '100%', marginTop: -10 },
  '& button': {
    cursor: 'pointer',
    width: 30,
    height: 30,
    margin: 5,
    background: 'transparent',
    border: '3px solid hsla(230,8%,60%,.5)',
    borderRadius: 2
  }
});

const StyledTooltip = styled(Tooltip)({
  '& .MuiTooltip-tooltip': {
    fontSize: 14,
  }
});

// Components
export function ControlForm() {
  const dispatch = useAppDispatch();
  const gender = useAppSelector((state) => state.draw.gender);
  const looking = useAppSelector((state) => state.draw.looking);

  return (
    <Forms>
      <FormControl>
        <Button
          className={`small ${!gender ? 'active' : ''}`}
          onClick={() => dispatch(setGender(0))}
        >
          <img src={maleImg} alt="male" />
        </Button>
        <Button
          className={`small ${gender ? 'active' : ''}`}
          onClick={() => dispatch(setGender(1))}
        >
          <img src={femaleImg} alt="female" />
        </Button>
      </FormControl>
      <FormControl className="right">
        <StyledTooltip title="direction left" arrow>
          <ColorEfxButton
            className={`small ${looking ? 'active' : ''}`}
            onClick={() => dispatch(setLook(true))}
          >
            <img src={arrowLeft} alt="left" />
          </ColorEfxButton>
        </StyledTooltip>
        <StyledTooltip title="direction right" arrow>
          <ColorEfxButton
            className={`small ${!looking ? 'active' : ''}`}
            onClick={() => dispatch(setLook(false))}
          >
            <img src={arrowRight} alt="right" />
          </ColorEfxButton>
        </StyledTooltip>
      </FormControl>
      <FormControl className="right random">
        <StyledTooltip title="direction left" arrow>
          <ColorEfxButton
            className={`small ${looking ? 'active' : ''}`}
            onClick={() => dispatch(setLook(true))}
          >
          </ColorEfxButton>
        </StyledTooltip>
        <StyledTooltip title="random" arrow>
          <Button
            className={`small`}
            onClick={() => dispatch(setRandom())}
          >
            <img src={randomImg} alt="random" />
          </Button>
        </StyledTooltip>
      </FormControl>
    </Forms>
  );
}

interface ControlTexturesProps {
  layer: LayerType;
  setLayer: (layer: LayerType) => void;
}

export function ControlTextures({ layer, setLayer }: ControlTexturesProps) {
  return (
    <Textures>
      <TextureButton
        onClick={() => setLayer(SKIN)}
        className={layer === SKIN ? 'active' : ''}
      >
        <img src={skinImg} alt="Skin" />
      </TextureButton>
      <TextureButton
        onClick={() => setLayer(HAIR)}
        className={layer === HAIR ? 'active' : ''}
      >
        <img src={hairImg} alt="Hair" />
      </TextureButton>
      <TextureButton
        onClick={() => setLayer(MOUTH)}
        className={layer === MOUTH ? 'active' : ''}
      >
        <img src={mouthImg} alt="Mouth" />
      </TextureButton>
      <TextureButton
        onClick={() => setLayer(WEAR)}
        className={layer === WEAR ? 'active' : ''}
      >
        <img src={wearImg} alt="Wear" />
      </TextureButton>
      <TextureButton
        onClick={() => setLayer(ADDON)}
        className={layer === ADDON ? 'active' : ''}
      >
        <img src={addonImg} alt="Addon" />
      </TextureButton>
    </Textures>
  );
}

interface ControlSelectionProps {
  layer: LayerType;
  variant?: string[];
  skins: string[][];
}

export function ControlSelection({ layer, variant, skins }: ControlSelectionProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const gender = useAppSelector((state) => state.draw.gender);
  const index = useAppSelector((state) => state.draw[layer as keyof typeof state.draw]) as { index: number; variant?: number };

  const validIndex = (array: unknown[], idx: number): number => {
    if (idx < 0) return array.length - 1;
    else if (idx > array.length - 1) return 0;
    else return idx;
  };

  const onIndexChange = (prop: 'index' | 'variant', value: number) => {
    const newIndex = (index[prop] ?? 0) + value;

    if (prop === 'variant' && variant) {
      dispatch(setIndex({
        name: layer as 'skin' | 'mouth' | 'hair' | 'wear' | 'addon',
        index: prop,
        data: validIndex(variant, newIndex)
      }));
      dispatch(setIndex({
        name: layer as 'skin' | 'mouth' | 'hair' | 'wear' | 'addon',
        index: 'index',
        data: 0
      }));
    } else {
      if (layer === SKIN) {
        dispatch(setIndex({
          name: layer,
          index: prop,
          data: validIndex(skins, newIndex)
        }));
      } else {
        const variantKey = variant ? variant[index.variant ?? 0] : layer;
        const assetArray = (assets as Record<string, unknown[][]>)[variantKey]?.[gender] ?? [];
        dispatch(setIndex({
          name: layer as 'skin' | 'mouth' | 'hair' | 'wear' | 'addon',
          index: prop,
          data: validIndex(assetArray, newIndex)
        }));
      }
    }
  };

  return (
    <Selection>
      <div className="labels">
        <div className="label">
          <SelectionText>{layer}</SelectionText>
        </div>
        {variant && (
          <div className="label">
            <SelectionText>
              {t(`variant:${variant[index.variant ?? 0]}`)}
            </SelectionText>
          </div>
        )}
      </div>
      {variant && (
        <SelectionControl>
          <StyledTooltip title="previous variation" arrow>
            <ColorEfxButton className="small" onClick={() => onIndexChange('variant', -1)}>
              <img src={arrowLeft} alt="left" />
            </ColorEfxButton>
          </StyledTooltip>
          <StyledTooltip title="next variation" arrow>
            <ColorEfxButton className="small" onClick={() => onIndexChange('variant', 1)}>
              <img src={arrowRight} alt="right" />
            </ColorEfxButton>
          </StyledTooltip>
        </SelectionControl>
      )}
      <SelectionControl>
        <StyledTooltip title={`previous ${layer}`} arrow>
          <ColorEfxButton className="small" onClick={() => onIndexChange('index', -1)}>
            <img src={arrowLeft} alt="left" />
          </ColorEfxButton>
        </StyledTooltip>
        <StyledTooltip title={`next ${layer}`} arrow>
          <ColorEfxButton className="small" onClick={() => onIndexChange('index', 1)}>
            <img src={arrowRight} alt="right" />
          </ColorEfxButton>
        </StyledTooltip>
      </SelectionControl>
    </Selection>
  );
}

interface ColorSelectionProps {
  layer: LayerType;
  setColor: (color: string) => void;
  setIndex: (index: number) => void;
  setOpen: (open: boolean) => void;
}

export function ColorSelection({ layer, setColor, setIndex, setOpen }: ColorSelectionProps) {
  const { t } = useTranslation('common');
  const peeps = useAppSelector((state) => state.draw.peeps);
  const peep = peeps[layerPeep[layer] as keyof typeof peeps] || [];

  const handleClick = (color: string, index: number) => {
    setIndex(index);
    setColor(color);
    setOpen(true);
  };

  return (
    <ColorBox>
      <div className="label">
        <SelectionText>{t('color:color_palette')}</SelectionText>
      </div>
      {peep.map((p, i) => (
        <button
          key={i}
          style={{ background: p[0] as string }}
          onClick={() => handleClick(p[0] as string, i)}
        />
      ))}
    </ColorBox>
  );
}

export type { LayerType };
export { layerPeep, SKIN, HAIR, WEAR, ADDON, MOUTH };
